import { Popup } from "@hatch-digital-nl/abs-storybook/legacy";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
    setError,
    setFormValues,
    setIsCreateEmployeeModalOpen,
    setIsPostCreateEmployeeSuccess,
} from "./CreateEmployeeModalSlice";
import { JSX, useEffect } from "react";

import { useGetFunctionGroupsQuery } from "../../common/api/functionGroup";
import Skeleton from "react-loading-skeleton";
import { useCreateEmployeeMutation } from "../../common/api/employee";
import { SubmitButtonComponent } from "./components/SubmitButtonComponent";
import { InputFieldComponent } from "./components/InputFieldComponent";
import { DateInputFieldComponent } from "./components/DateInputFieldComponent";
import { SelectFieldComponent } from "./components/SelectFieldComponent";
import { setEstablishmentFormValues } from "./utils/setEstablishmentFormValues";
import { FormWrapper } from "./components/FormWrapper";
import { useGetEstablishmentByIdQuery } from "../../common/api/establishment";
import { useEstablishmentId } from "../../common/utils/useEstablishmentId";

export const CreateEmployeeModalContainer = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const establishmentId = useEstablishmentId();
    const { data: establishmentData, isLoading: establishmentDataIsLoading } =
        useGetEstablishmentByIdQuery({
            id: establishmentId,
        });
    const { data: functionGroupData, isLoading: functionGroupDataIsloading } =
        useGetFunctionGroupsQuery({ items_per_page: 999 });
    const [
        postCreateEmployee,
        {
            isLoading: isPostCreateEmployeeLoading,
            isSuccess: isPostCreateEmployeeSuccess,
        },
    ] = useCreateEmployeeMutation();
    const {
        formValues,
        error,
        functionGroupInputValue,
        isCreateEmployeeModalOpen,
        isAddAgainButtonClicked,
    } = useAppSelector((state) => state.createEmployeeModal);

    useEffect(() => {
        setEstablishmentFormValues(dispatch, establishmentData);
    }, [isCreateEmployeeModalOpen, establishmentData, dispatch]);

    useEffect(() => {
        if (isPostCreateEmployeeSuccess) {
            dispatch(setIsPostCreateEmployeeSuccess(true));
        }
    }, [isPostCreateEmployeeSuccess, dispatch]);

    return (
        <Popup
            className={"c-create-employee-modal"}
            title={"Medewerker toevoegen"}
            onCloseClick={() => {
                dispatch(setIsCreateEmployeeModalOpen(false));
                dispatch(setError(null));
            }}
        >
            {error && <p className={"c-error"}>{error}</p>}
            {functionGroupDataIsloading && establishmentDataIsLoading ? (
                <Skeleton count={9} height={"5.5rem"} borderRadius={"0.8rem"} />
            ) : (
                <FormWrapper
                    formValues={formValues}
                    dispatch={dispatch}
                    postCreateEmployee={postCreateEmployee}
                    isAddAgainButtonClicked={isAddAgainButtonClicked}
                >
                    <InputFieldComponent
                        id={1}
                        type={"text"}
                        title={"Voornaam"}
                        required={true}
                        onChange={(value) =>
                            dispatch(
                                setFormValues({
                                    ...formValues,
                                    firstName: value,
                                }),
                            )
                        }
                        value={formValues?.firstName}
                        errorMessage={"Voornaam is verplicht"}
                    />
                    <div
                        className={
                            "c-create-employee-modal__lastname-insertion-wrapper c-edit-employee-modal__form-group"
                        }
                    >
                        <InputFieldComponent
                            id={10}
                            type={"text"}
                            title={"Tussenvoegsel"}
                            onChange={(value) =>
                                dispatch(
                                    setFormValues({
                                        ...formValues,
                                        insertion: value,
                                    }),
                                )
                            }
                            value={formValues?.insertion}
                            showOptional={false}
                        />
                        <InputFieldComponent
                            id={11}
                            type={"text"}
                            title={"Achternaam"}
                            required={true}
                            onChange={(value) =>
                                dispatch(
                                    setFormValues({
                                        ...formValues,
                                        lastName: value,
                                    }),
                                )
                            }
                            value={formValues?.lastName}
                            errorMessage={"Achternaam is verplicht"}
                        />
                    </div>
                    <SelectFieldComponent
                        dispatch={dispatch}
                        formValues={formValues}
                        functionGroupInputValue={functionGroupInputValue}
                        functionGroupOptions={
                            functionGroupData
                                ? functionGroupData.data.map((item) => ({
                                      label: item.name,
                                      value: item.id.toString(),
                                  }))
                                : []
                        }
                    />
                    <DateInputFieldComponent
                        formValues={formValues}
                        dispatch={dispatch}
                    />
                    <InputFieldComponent
                        id={11}
                        type={"text"}
                        title={"Geboorteplaats"}
                        required={false}
                        onChange={(value) =>
                            dispatch(
                                setFormValues({
                                    ...formValues,
                                    placeOfBirth: value,
                                }),
                            )
                        }
                        value={formValues?.placeOfBirth}
                    />
                    <InputFieldComponent
                        id={2}
                        type={"text"}
                        title={"Vestiging"}
                        disabled={true}
                        required={true}
                        value={formValues?.establishment?.selectedValue}
                        errorMessage={"Vestiging is verplicht"}
                    />
                    <InputFieldComponent
                        id={3}
                        type={"email"}
                        title={"Privé e-mailadres"}
                        required={true}
                        errorMessage={"Privé e-mailadres is verplicht"}
                        value={formValues?.privateEmail}
                        onChange={(value) =>
                            dispatch(
                                setFormValues({
                                    ...formValues,
                                    privateEmail: value,
                                }),
                            )
                        }
                    />
                    <InputFieldComponent
                        id={4}
                        type={"email"}
                        title={"Zakelijke e-mailadres"}
                        value={formValues?.businessEmail}
                        onChange={(value) =>
                            dispatch(
                                setFormValues({
                                    ...formValues,
                                    businessEmail: value,
                                }),
                            )
                        }
                    />
                    <InputFieldComponent
                        id={5}
                        type={"text"}
                        required={true}
                        title={"Telefoonnummer"}
                        errorMessage={"Telefoonnummer is verplicht"}
                        value={formValues?.telephone}
                        onChange={(value) =>
                            dispatch(
                                setFormValues({
                                    ...formValues,
                                    telephone: value,
                                }),
                            )
                        }
                        onInput={(event) => {
                            const target = event.target as HTMLInputElement;
                            if (target.value.length > 10) {
                                target.value = target.value.slice(0, 10);
                            }
                        }}
                    />
                    <SubmitButtonComponent
                        isPostCreateEmployeeLoading={
                            isPostCreateEmployeeLoading
                        }
                        dispatch={dispatch}
                    />
                </FormWrapper>
            )}
        </Popup>
    );
};
